import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, Info } from 'lucide-react';
import {
  LIMITS,
  TASK_COMPLEXITIES,
  TASK_COMPLEXITY_META,
  TASK_PRIORITIES,
  TASK_PRIORITY_META,
  type TaskComplexity,
  type TaskPriority,
} from '@ssn/shared';
import { useCreateTask, useReferenceData } from '@/lib/queries';
import { useSession } from '@/session/SessionProvider';
import { PageHeader, Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { Select, TextArea, TextInput } from '@/components/ui/Form';
import { Callout, InlineError } from '@/components/ui/Feedback';
import { ApiError } from '@/lib/api';
import { fromLocalInputValue } from '@/lib/format';

/**
 * Task creation (§4).
 *
 * A developer creating their own task has it assigned to them automatically —
 * there is no assignee control unless they are authorised to assign to somebody
 * else, because offering a field the server will refuse is worse than not
 * offering it at all.
 */
export function CreateTaskPage() {
  const navigate = useNavigate();
  const { can, session } = useSession();
  const reference = useReferenceData();
  const createTask = useCreateTask();

  const canAssignOthers = can('task:create:assign_other');
  const canSetCritical = can('task:priority:critical');

  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'MEDIUM' as TaskPriority,
    complexity: '' as TaskComplexity | '',
    projectId: '',
    categoryId: '',
    deadline: '',
    estimatedHours: '',
    tags: '',
    assigneeId: '',
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const error = createTask.error instanceof ApiError ? createTask.error : null;

  const submit = (event: FormEvent) => {
    event.preventDefault();

    createTask.mutate(
      {
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        complexity: form.complexity || null,
        projectId: form.projectId || null,
        categoryId: form.categoryId || null,
        deadline: fromLocalInputValue(form.deadline),
        estimatedHours: form.estimatedHours ? Number(form.estimatedHours) : null,
        tags: form.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        assigneeId: canAssignOthers && form.assigneeId ? form.assigneeId : null,
      },
      {
        onSuccess: (result) => navigate(`/tasks/${result.task.reference}`),
      },
    );
  };

  return (
    <>
      <PageHeader
        title="Create a task"
        description="Describe the work clearly enough that a director can review it later without asking you what it was."
      />

      <form onSubmit={submit} className="grid max-w-4xl gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Panel title="The work">
            <div className="space-y-4">
              <TextInput
                label="Title"
                required
                autoFocus
                value={form.title}
                onChange={(event) => set('title', event.target.value)}
                maxLength={LIMITS.titleMax}
                placeholder="MDT Dispatch Improvements"
                error={error?.fieldError('title')}
                hint="A short, specific summary. This is what appears in lists and Discord."
              />

              <TextArea
                label="Description"
                value={form.description}
                onChange={(event) => set('description', event.target.value)}
                maxLength={LIMITS.descriptionMax}
                className="min-h-[180px]"
                placeholder={
                  'What needs building, and what does "done" look like?\n\nInclude anything a reviewer will need: requirements, constraints, links, how to test it.'
                }
                error={error?.fieldError('description')}
              />
            </div>
          </Panel>

          <Panel title="Classification" description="Used for filtering, analytics and reporting.">
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Project"
                value={form.projectId}
                onChange={(event) => set('projectId', event.target.value)}
                placeholder="No project"
                options={(reference.data?.projects ?? []).map((project) => ({
                  value: project.id,
                  label: project.name,
                }))}
              />
              <Select
                label="Category"
                value={form.categoryId}
                onChange={(event) => set('categoryId', event.target.value)}
                placeholder="No category"
                options={(reference.data?.categories ?? []).map((category) => ({
                  value: category.id,
                  label: category.name,
                }))}
              />
              <TextInput
                label="Tags"
                value={form.tags}
                onChange={(event) => set('tags', event.target.value)}
                placeholder="dispatch, mdt, ui"
                hint="Comma separated."
                error={error?.fieldError('tags')}
                containerClassName="sm:col-span-2"
              />
            </div>
          </Panel>
        </div>

        <div className="space-y-5">
          <Panel title="Priority and sizing">
            <div className="space-y-4">
              <Select
                label="Priority"
                required
                value={form.priority}
                onChange={(event) => set('priority', event.target.value as TaskPriority)}
                options={TASK_PRIORITIES.map((priority) => ({
                  value: priority,
                  label: TASK_PRIORITY_META[priority].label,
                  disabled: TASK_PRIORITY_META[priority].restricted && !canSetCritical,
                }))}
                hint={TASK_PRIORITY_META[form.priority].description}
                error={error?.fieldError('priority')}
              />

              {!canSetCritical && (
                <Callout tone="info" icon={<Flame aria-hidden className="size-3.5" />}>
                  Critical priority is reserved for Senior Developers and Directors, so that it keeps
                  its meaning. Ask one of them if this really is blocking.
                </Callout>
              )}

              <Select
                label="Estimated complexity"
                value={form.complexity}
                onChange={(event) => set('complexity', event.target.value as TaskComplexity | '')}
                placeholder="Not sized"
                options={TASK_COMPLEXITIES.map((complexity) => ({
                  value: complexity,
                  label: `${TASK_COMPLEXITY_META[complexity].label} — ${TASK_COMPLEXITY_META[complexity].description}`,
                }))}
              />

              <TextInput
                label="Estimated hours"
                type="number"
                min={0}
                step="0.5"
                value={form.estimatedHours}
                onChange={(event) => set('estimatedHours', event.target.value)}
                placeholder="Optional"
                hint="Actual time is measured from the workflow — this is only your estimate."
                error={error?.fieldError('estimatedHours')}
              />

              <TextInput
                label="Deadline"
                type="datetime-local"
                value={form.deadline}
                onChange={(event) => set('deadline', event.target.value)}
                error={error?.fieldError('deadline')}
              />
            </div>
          </Panel>

          <Panel title="Assignment">
            {canAssignOthers ? (
              <Select
                label="Assign to"
                value={form.assigneeId}
                onChange={(event) => set('assigneeId', event.target.value)}
                placeholder={`${session?.user.displayName ?? 'Me'} (default)`}
                options={(reference.data?.assignableUsers ?? []).map((user) => ({
                  value: user.id,
                  label: user.displayName,
                }))}
                hint="They will be sent a Discord DM."
                error={error?.fieldError('assigneeId')}
              />
            ) : (
              <Callout tone="info" icon={<Info aria-hidden className="size-3.5" />}>
                This task will be assigned to you automatically. A director can reassign it later.
              </Callout>
            )}
          </Panel>

          <InlineError error={createTask.error} />

          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              loading={createTask.isPending}
              disabled={form.title.trim().length < LIMITS.titleMin}
            >
              Create task
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
